import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicNavbar from "../../components/PublicNavbar";
import Footer from "../../components/Footer";
import { api, formatEUR, formatDateFR, photoUrl } from "../../lib/api";
import { CalendarCheck, Car, MapPin, Clock, ChevronRight, Loader2, FileText } from "lucide-react";

export default function ClientBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/client/bookings")
      .then(({ data }) => setBookings(data.bookings || []))
      .finally(() => setLoading(false));
  }, []);

  const statusMap = {
    confirmed: { label: "Confirmée", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    completed: { label: "Terminée", color: "bg-slate-100 text-slate-600 border-slate-200" },
    pending: { label: "En attente", color: "bg-amber-50 text-amber-700 border-amber-200" },
    cancelled: { label: "Annulée", color: "bg-red-50 text-red-600 border-red-200" },
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicNavbar />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="overline mb-1">Mon compte</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-slate-900" data-testid="client-bookings-title">
              Mes réservations
            </h1>
          </div>
        </div>

        {loading ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-violet-600" />
          </div>
        ) : bookings.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-slate-200" data-testid="no-bookings">
            <CalendarCheck className="h-12 w-12 text-slate-300 mx-auto" />
            <h2 className="font-heading text-xl font-medium text-slate-900 mt-4">Aucune réservation</h2>
            <p className="text-sm text-slate-500 mt-2 max-w-sm mx-auto">
              Les réservations effectuées auprès de votre loueur apparaîtront ici.
            </p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="bookings-list">
            {bookings.map((b) => {
              const st = statusMap[b.status] || statusMap.confirmed;
              return (
                <div
                  key={b.id}
                  className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 flex flex-col sm:flex-row gap-4 sm:items-center"
                  data-testid={`booking-${b.id}`}
                >
                  <div className="h-20 w-28 rounded-xl overflow-hidden bg-slate-100 shrink-0">
                    {b.vehicle_photo && (
                      <img src={photoUrl(b.vehicle_photo)} alt="" className="h-full w-full object-cover" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-heading font-medium text-slate-900">{b.vehicle_name}</h3>
                      <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${st.color}`}>
                        {st.label}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 mt-2">
                      <span className="inline-flex items-center gap-1">
                        <CalendarCheck className="h-3 w-3" />
                        {formatDateFR(b.start_date)} - {formatDateFR(b.end_date)}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {b.days} jour{b.days > 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-heading text-xl font-medium text-slate-900">{formatEUR(b.total)}</div>
                    <div className="text-xs text-slate-500">Total payé</div>
                    <div className="flex items-center gap-2 mt-2">
                      <Link to={`/contract/${b.id}`} className="inline-flex items-center gap-1 text-xs font-medium text-violet-600 hover:underline" data-testid={`contract-link-${b.id}`}>
                        <FileText className="h-3 w-3" /> Contrat
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
